import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, switchMap, take, tap, throwError } from "rxjs";
import { ClassroomWithStockRequests } from "./classroom.types";
import { ApiResponse } from "src/app/core/utils/api-response";
import { environment } from "src/environments/environment";
import { HttpClient } from "@angular/common/http";
import { Classroom } from "src/app/core/classroom/classroom-shared.types";

@Injectable({ providedIn: 'root' })
export class ClassroomService {
    // Base URLs
    private readonly BASE_URL = environment.apiUrl;
    private readonly CLASSROOMS_URL = `${this.BASE_URL}/classrooms`;

    constructor(private _httpClient: HttpClient) { }

    // BehaviorSubjects
    private _classroom: BehaviorSubject<Classroom | null> = new BehaviorSubject<Classroom | null>(null);
    private _classrooms: BehaviorSubject<Classroom[] | null> = new BehaviorSubject<Classroom[] | null>(null);


    // Add new BehaviorSubject
    private _classroomsWithRequests: BehaviorSubject<ClassroomWithStockRequests[] | null> =
        new BehaviorSubject<ClassroomWithStockRequests[] | null>(null);

    // Add accessor
    get classroomsWithRequests$(): Observable<ClassroomWithStockRequests[] | null> {
        return this._classroomsWithRequests.asObservable();
    }

    // Accessors
    get classroom$(): Observable<Classroom | null> {
        return this._classroom.asObservable();
    }

    get classrooms$(): Observable<Classroom[] | null> {
        return this._classrooms.asObservable();
    }

    // Add method to fetch classrooms with requests
    getClassroomsWithRequests(schoolCode: string): Observable<ApiResponse<ClassroomWithStockRequests[]>> {
        return this._httpClient
            .get<ApiResponse<ClassroomWithStockRequests[]>>(`${this.BASE_URL}/inventory/classroom/school/${schoolCode}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._classroomsWithRequests.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch classrooms with requests:', error);
                    const errorResponse: ApiResponse<ClassroomWithStockRequests[]> = error?.error || {
                        success: false,
                        message: error.message || 'Failed to fetch classrooms with requests',
                        data: null,
                        timestamp: new Date()
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

        getClassroomsPaged(
            schoolCode: string,
            search: string,
            page: number,
            size: number,
            sortField: string = '',
            sortOrder: number = 1,
            filters: { [key: string]: any } = {}
        ): Observable<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>> {
            let url = `${this.BASE_URL}/inventory/classroom/school/${schoolCode}/paged?search=${encodeURIComponent(search)}&page=${page}&size=${size}`;
            // if (search) url += `&search=${encodeURIComponent(search)}`;
            if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;

            // Add filters as query params (array or object)
            Object.keys(filters).forEach(key => {
                const value = filters[key];
                if (Array.isArray(value)) {
                    value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
                } else if (value !== undefined && value !== null && value !== '') {
                    url += `&${key}=${encodeURIComponent(value)}`;
                }
            });

            return this._httpClient.get<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>>(url)
                .pipe(
                    tap((response) => {
                        if (response.success && response.data && Array.isArray(response.data.content)) {
                            this._classroomsWithRequests.next(response.data.content);
                        }
                    }),
                    catchError((error) => {
                        // ...error handling...
                        return throwError(() => error);
                    })
                );
        }

    createClassroom(classroom: Classroom): Observable<ApiResponse<Classroom>> {
        return this.classrooms$.pipe(
            take(1),
            switchMap((classrooms) =>
                this._httpClient
                    .post<ApiResponse<Classroom>>(this.CLASSROOMS_URL, classroom)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                this._classrooms.next([response.data, ...(classrooms || [])]);

                                // Update _classroomsWithRequests
                                const currentWithRequests = this._classroomsWithRequests.value || [];
                                const newClassroomWithRequests: ClassroomWithStockRequests = {
                                    classroom: response.data,
                                    stockRequests: {
                                        code: '',
                                        requestPending: 0,
                                        requestCompleted: 0,
                                        consumption: 0
                                    }
                                };
                                this._classroomsWithRequests.next([newClassroomWithRequests, ...currentWithRequests]);

                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to create classroom:', error);
                            const errorResponse: ApiResponse<Classroom> = error?.error || {
                                success: false,
                                message: error.message || 'Failed to create classroom',
                                data: null,
                                timestamp: new Date()
                            };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }


    updateClassroom(classroom: Classroom): Observable<ApiResponse<Classroom>> {
        return this.classrooms$.pipe(
            take(1),
            switchMap((classrooms) =>
                this._httpClient
                    .put<ApiResponse<Classroom>>(`${this.CLASSROOMS_URL}/${classroom.code}`, classroom)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                const index = classrooms?.findIndex(item => item.code === classroom.code) ?? -1;
                                if (index !== -1 && classrooms) {
                                    classrooms[index] = response.data;
                                    this._classrooms.next(classrooms);
                                }
                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to update classroom:', error);
                            const errorResponse: ApiResponse<Classroom> = error?.error || {
                                success: false,
                                message: error.message || 'Failed to update classroom',
                                data: null,
                                timestamp: new Date()
                            };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    deleteClassroom(code: string): Observable<ApiResponse<void>> {
        return this.classrooms$.pipe(
            take(1),
            switchMap((classrooms) =>
                this._httpClient
                    .delete<ApiResponse<void>>(`${this.CLASSROOMS_URL}/${code}`)
                    .pipe(
                        tap((response) => {
                            if (response.success && classrooms) {
                                const filteredClassrooms = classrooms.filter(item => item.code !== code);
                                this._classrooms.next(filteredClassrooms);
                            }
                            if(response.success) {
                                // Remove from classroomsWithRequests
                                const classroomsWithRequests = this._classroomsWithRequests.value || [];
                                const filteredWithRequests = classroomsWithRequests.filter(item => item.classroom?.code !== code);
                                this._classroomsWithRequests.next(filteredWithRequests);
                            }


                        }),
                        catchError((error) => {
                            console.error('Failed to delete classroom:', error);
                            const errorResponse: ApiResponse<void> = error?.error || {
                                success: false,
                                message: error.message || 'Failed to delete classroom',
                                data: null,
                                timestamp: new Date()
                            };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    getClassroomsPagedForStaff(
        schoolCode: string,
        staffCode: string,
        search: string,
        page: number,
        size: number,
        sortField: string = '',
        sortOrder: number = 1,
        filters: { [key: string]: any } = {}
    ): Observable<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>> {
        let url = `${this.BASE_URL}/inventory/classroom/school/${schoolCode}/paged/staff/${staffCode}?search=${encodeURIComponent(search)}&page=${page}&size=${size}`;
        if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;

        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (Array.isArray(value)) {
                value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
            } else if (value !== undefined && value !== null && value !== '') {
                url += `&${key}=${encodeURIComponent(value)}`;
            }
        });

        return this._httpClient.get<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>>(url)
            .pipe(
                tap((response) => {
                    if (response.success && response.data && Array.isArray(response.data.content)) {
                        this._classroomsWithRequests.next(response.data.content);
                    }
                }),
                catchError((error) => {
                    return throwError(() => error);
                })
            );
    }


}
